"use client";

import { orpcClient } from "@shared/lib/orpc";
import { useForm, useStore } from "@tanstack/react-form";
import { Field, FieldError, FieldLabel } from "@ui/components/field";
import { Input } from "@ui/components/input";
import { Textarea } from "@ui/components/textarea";
import { cn } from "@ui/lib";
import { CheckIcon } from "lucide-react";
import { useState } from "react";
import { z } from "zod";
import { identify, track } from "../analytics";
import { mkButton } from "../components/MkButton";

const CHANNELS = [
	{ value: "app", label: "In my app" },
	{ value: "whatsapp", label: "WhatsApp" },
	{ value: "voice", label: "Voice calls" },
] as const;

/** Fields are white on the form's grey panel: no borders, a green ring when focused. */
const FIELD =
	"rounded-xl border-0 bg-(--mk-white) px-4 text-[16px] shadow-none focus-visible:ring-2 focus-visible:ring-(--mk-signal) data-[slot=input]:h-12";

type Channel = (typeof CHANNELS)[number]["value"];

const required = (label: string, min = 2) =>
	z.string().trim().min(min, `Enter your ${label}`);

/** The "Book a demo" form: sent to Hephon by email, with a Telegram ping. */
export function DemoForm() {
	const [sent, setSent] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const form = useForm({
		defaultValues: {
			name: "",
			email: "",
			company: "",
			country: "",
			channels: [] as Channel[],
			message: "",
			website: "",
		},
		onSubmit: async ({ value }) => {
			setError(null);
			try {
				await orpcClient.contactForm.submit({
					name: value.name,
					email: value.email,
					company: value.company,
					...(value.country ? { country: value.country } : {}),
					...(value.channels.length
						? { channels: value.channels }
						: {}),
					...(value.message ? { message: value.message } : {}),
					...(value.website ? { website: value.website } : {}),
				});
				identify(value.email, {
					name: value.name,
					company: value.company,
					...(value.country ? { country: value.country } : {}),
				});
				track("demo_requested", {
					channels: value.channels,
					country: value.country || undefined,
					has_message: Boolean(value.message),
				});
				setSent(true);
			} catch {
				setError(
					"That did not go through. Try again, or write to hello@hephon.ai.",
				);
			}
		},
	});
	const submitting = useStore(form.store, (s) => s.isSubmitting);

	if (sent) {
		return (
			<div
				className="flex flex-col items-center gap-4 py-16 text-center"
				aria-live="polite"
			>
				<span className="grid size-14 place-items-center rounded-full bg-(--mk-signal) text-(--mk-white)">
					<CheckIcon className="size-6" aria-hidden />
				</span>
				<h2 className="mk-h3">Thank you. We will be in touch.</h2>
				<p className="max-w-sm text-(--mk-muted)">
					Expect a reply within one working day, usually much sooner,
					to set up a demo on your own catalog.
				</p>
			</div>
		);
	}

	return (
		<form
			noValidate
			onSubmit={(event) => {
				event.preventDefault();
				void form.handleSubmit();
			}}
			className="grid gap-5"
		>
			<div className="grid gap-5 sm:grid-cols-2">
				<form.Field
					name="name"
					validators={{ onBlur: required("name") }}
				>
					{(field) => {
						const invalid =
							field.state.meta.isTouched &&
							field.state.meta.errors.length > 0;
						return (
							<Field data-invalid={invalid || undefined}>
								<FieldLabel htmlFor="demo-name">
									Your name
								</FieldLabel>
								<Input
									className={FIELD}
									id="demo-name"
									autoComplete="name"
									value={field.state.value}
									onChange={(e) =>
										field.handleChange(e.target.value)
									}
									onBlur={field.handleBlur}
									aria-invalid={invalid || undefined}
								/>
								{invalid ? (
									<FieldError
										errors={field.state.meta.errors}
									/>
								) : null}
							</Field>
						);
					}}
				</form.Field>
				<form.Field
					name="email"
					validators={{
						onBlur: z.string().trim().email("Enter a work email"),
					}}
				>
					{(field) => {
						const invalid =
							field.state.meta.isTouched &&
							field.state.meta.errors.length > 0;
						return (
							<Field data-invalid={invalid || undefined}>
								<FieldLabel htmlFor="demo-email">
									Work email
								</FieldLabel>
								<Input
									className={FIELD}
									id="demo-email"
									type="email"
									autoComplete="email"
									value={field.state.value}
									onChange={(e) =>
										field.handleChange(e.target.value)
									}
									onBlur={field.handleBlur}
									aria-invalid={invalid || undefined}
								/>
								{invalid ? (
									<FieldError
										errors={field.state.meta.errors}
									/>
								) : null}
							</Field>
						);
					}}
				</form.Field>
				<form.Field
					name="company"
					validators={{ onBlur: required("company") }}
				>
					{(field) => {
						const invalid =
							field.state.meta.isTouched &&
							field.state.meta.errors.length > 0;
						return (
							<Field data-invalid={invalid || undefined}>
								<FieldLabel htmlFor="demo-company">
									Company
								</FieldLabel>
								<Input
									className={FIELD}
									id="demo-company"
									autoComplete="organization"
									value={field.state.value}
									onChange={(e) =>
										field.handleChange(e.target.value)
									}
									onBlur={field.handleBlur}
									aria-invalid={invalid || undefined}
								/>
								{invalid ? (
									<FieldError
										errors={field.state.meta.errors}
									/>
								) : null}
							</Field>
						);
					}}
				</form.Field>
				<form.Field name="country">
					{(field) => (
						<Field>
							<FieldLabel htmlFor="demo-country">
								Country
							</FieldLabel>
							<Input
								className={FIELD}
								id="demo-country"
								autoComplete="country-name"
								value={field.state.value}
								onChange={(e) =>
									field.handleChange(e.target.value)
								}
							/>
						</Field>
					)}
				</form.Field>
			</div>

			<form.Field name="channels">
				{(field) => (
					<fieldset>
						<legend className="mb-2 font-medium text-sm">
							Where should the assistant work?
						</legend>
						<div className="flex flex-wrap gap-2">
							{CHANNELS.map((channel) => {
								const on = field.state.value.includes(
									channel.value,
								);
								return (
									<button
										key={channel.value}
										type="button"
										aria-pressed={on}
										onClick={() =>
											field.handleChange(
												on
													? field.state.value.filter(
															(c) =>
																c !==
																channel.value,
														)
													: [
															...field.state
																.value,
															channel.value,
														],
											)
										}
										className={cn(
											"inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[15px] outline-none transition-colors duration-(--duration-hover) focus-visible:ring-2 focus-visible:ring-(--mk-signal)",
											on
												? "bg-(--mk-ink) text-(--mk-white)"
												: "bg-(--mk-white) text-(--mk-ink-2) hover:bg-(--mk-mist-2)",
										)}
									>
										{on ? (
											<CheckIcon
												className="size-3.5"
												aria-hidden
											/>
										) : null}
										{channel.label}
									</button>
								);
							})}
						</div>
					</fieldset>
				)}
			</form.Field>

			<form.Field name="message">
				{(field) => (
					<Field>
						<FieldLabel htmlFor="demo-message">
							Anything we should know? (optional)
						</FieldLabel>
						<Textarea
							className={FIELD}
							id="demo-message"
							rows={4}
							placeholder="Your stores, platform, languages, timeline"
							value={field.state.value}
							onChange={(e) => field.handleChange(e.target.value)}
						/>
					</Field>
				)}
			</form.Field>

			{/* Not for people: bots fill every field they find. */}
			<form.Field name="website">
				{(field) => (
					<div
						aria-hidden
						className="absolute -left-[9999px] h-0 overflow-hidden"
					>
						<label htmlFor="demo-website">Website</label>
						<input
							id="demo-website"
							tabIndex={-1}
							autoComplete="off"
							value={field.state.value}
							onChange={(e) => field.handleChange(e.target.value)}
						/>
					</div>
				)}
			</form.Field>

			{error ? (
				<p role="alert" className="text-(--mk-stop) text-sm">
					{error}
				</p>
			) : null}
			<button
				type="submit"
				disabled={submitting}
				className={mkButton({
					size: "lg",
					className: "w-full disabled:opacity-60",
				})}
			>
				{submitting ? "Sending…" : "Book my demo"}
			</button>
			<p className="text-center text-(--mk-faint) text-xs">
				We use your details only to arrange the demo. See our{" "}
				<a
					href="/legal/privacy"
					className="underline underline-offset-2"
				>
					privacy policy
				</a>
				.
			</p>
		</form>
	);
}
